import React, { FC, useEffect, useState } from 'react'

import { Box, Button, Stack, Typography, alpha, useTheme } from '@mui/material'
import { Switch } from '@repo/ui'
import { Link2, X } from 'lucide-react'
import { CertificateViewAccess } from '@repo/types'
import type { TCertificateViewVisibility } from '@repo/types'

import { DrawerPopup } from 'components/UI/DrawerPopup'
import { useSnackbar } from 'components/Snackbar'
import { FieldLabel } from 'components/UI/FieldLabel'
import { Input } from 'components/UI'
import {
  parseInviteEmails,
  useCertificateViewViewers,
  useInviteCertificateViewers,
  useRemoveCertificateViewer,
  type CertificateView,
} from 'hooks/api/useCertificateViews'
import { timeAgo } from 'utils/date'

/**
 * VERA-299 — the module list a view exposes, with the copy shown next to each toggle.
 *
 * Ordered as drawn. `show_ownership` leads because it is the sensitive one (it
 * reveals the owner's name and email to anyone holding the link).
 */
/**
 * VERA-299 — the privacy toggles, GROUPED by the certificate section each one governs.
 *
 * The grouping is the point: a flat list left it ambiguous whether "Show your
 * ownership" and "Show ownership history" were the same concern (they are not — one is
 * YOUR identity, the other is other people's). Naming the section each toggle belongs
 * to makes the mapping to what a visitor sees explicit.
 *
 * Sale prices sit under PROVENANCE because they render inside the history rows. Scans
 * are their OWN group, not a sub-setting of provenance: they record the item's physical
 * journey during your ownership, which is a different fact about different people.
 */
const MODULE_GROUPS: {
  section: string
  modules: {
    key: keyof TCertificateViewVisibility
    title: string
    description: string
  }[]
}[] = [
  {
    section: 'Ownership',
    modules: [
      {
        key: 'show_ownership',
        title: 'Show your ownership',
        description: 'Your name and email are visible to anyone who opens this link.',
      },
    ],
  },
  {
    section: 'Provenance',
    modules: [
      {
        key: 'show_provenance',
        title: 'Show ownership history',
        // VRG-496 — was "Everyone who has owned this item before you", which stopped
        // being true when the public chain was stripped of its identities. Previous
        // owners are never named to a visitor now, whatever this is set to, so
        // promising otherwise would tell the owner they are publishing something they
        // are not. What the toggle still controls is whether the chain appears at all.
        description:
          'How many owners it has had, and when it changed hands. Previous owners are never named.',
      },
      {
        key: 'show_prices',
        title: 'Show sale prices',
        description: 'What this item sold for at each change of ownership.',
      },
    ],
  },
  {
    section: 'Scans',
    modules: [
      {
        key: 'show_activity',
        title: 'Show scans',
        description:
          "The location and identity of every scan of this item since you've been the owner.",
      },
    ],
  },
]

/** How many viewers show before "Show all" — mirrors PublicCertificateSections. */
const PREVIEW_COUNT = 3

interface ViewSettingsDrawerProps {
  certificateId: string
  view: CertificateView | null
  open: boolean
  /**
   * VERA-300 — `create` renders the same fields but writes nothing until the
   * user hits Create, and hides Access (there is no view to invite anyone to
   * yet). Reusing this component rather than forking one is the point: create
   * and edit can never drift apart on the fields they expose.
   */
  mode?: 'create' | 'edit'
  isSaving?: boolean
  onClose: () => void
  /**
   * Change who may open the link. Applied immediately, not on Save, because the rest of
   * the Access section behaves that way and a deferred access change would misstate
   * when access actually changed.
   */
  onAccessChange?: (access: CertificateViewAccess) => void
  onSave: (values: {
    name: string
    visibility: TCertificateViewVisibility
    /** Create mode only: emails to invite once the view exists. Empty in edit. */
    inviteEmails: string[]
  }) => void
}

export const ViewSettingsDrawer: FC<ViewSettingsDrawerProps> = ({
  certificateId,
  view,
  open,
  mode = 'edit',
  isSaving,
  onClose,
  onSave,
  onAccessChange,
}) => {
  const theme = useTheme()
  const { openSnackbar } = useSnackbar()

  const [emailsRaw, setEmailsRaw] = useState('')
  const [name, setName] = useState('')
  const [showAllViewers, setShowAllViewers] = useState(false)

  const { data: viewers } = useCertificateViewViewers(certificateId, view?.id)
  const { mutate: invite, isPending: isInviting } = useInviteCertificateViewers(
    certificateId,
    view?.id
  )
  const { mutate: removeViewer } = useRemoveCertificateViewer(certificateId, view?.id)

  const [draft, setDraft] = useState<TCertificateViewVisibility>({
    show_ownership: false,
    show_provenance: false,
    show_prices: false,
    show_activity: false,
  })

  // Re-seed whenever a different view is opened, so the drawer never shows the
  // previous view's toggles for a moment. In create mode there is no view to
  // seed from, so every open starts blank instead — `open` is in the deps
  // because the same drawer instance is reused across repeated create/cancel
  // cycles, and `view` never changes to re-trigger this otherwise.
  useEffect(() => {
    if (mode === 'create') {
      setName('')
      setDraft({
        show_ownership: false,
        show_provenance: false,
        show_prices: false,
        show_activity: false,
      })
    } else if (view) {
      setName(view.name ?? '')
      setDraft(view.visibility)
    }
    setEmailsRaw('')
    setShowAllViewers(false)
  }, [view?.id, view?.updated_at, mode, open])

  const sendInvites = () => {
    const emails = parseInviteEmails(emailsRaw)

    if (emails.length === 0) return

    invite(emails, {
      onSuccess: (result) => {
        setEmailsRaw('')

        // Report what actually happened, not what was submitted. Two ways the old
        // "N invites sent" lied: re-inviting an existing viewer is a server-side no-op
        // (nothing sent), and a mail failure still granted access (row written before
        // the send). Access and delivery are reported separately because only the
        // first is guaranteed.
        const added = result.invited.length
        const failed = result.email_failures.length

        if (added === 0) {
          openSnackbar({
            severity: 'info',
            title: emails.length === 1 ? 'Already has access' : 'Already have access',
          })
          return
        }

        if (failed > 0) {
          openSnackbar({
            severity: 'warning',
            title:
              failed === added
                ? 'Access granted, but the invite email could not be sent'
                : `Access granted. ${failed} invite email${failed === 1 ? '' : 's'} could not be sent`,
          })
          return
        }

        /*
          No success toast: the invitee appears in the Viewers list a few rows
          below, which is the confirmation.

          The two paths above KEEP their toasts and are not the same case —
          "already has access" is a no-op with nothing to show, and a delivery
          failure is invisible by nature (the row was written; the mail was not
          sent). Those have no surface other than a toast. This one did.
        */
      },
    })
  }

  const setModule = (key: keyof TCertificateViewVisibility, checked: boolean) => {
    // Every module is independent: ownership (your identity), provenance (other
    // people's identities), prices (transaction facts), scans (the item's journey).
    // No toggle clears another.
    //
    // This previously cleared `show_prices` whenever provenance went off, which made
    // the anonymised price-history state — amounts without owners — unreachable from
    // the UI even though the model and the server both support it.
    setDraft((prev) => ({ ...prev, [key]: checked }))
  }

  const copyLink = () => {
    if (!view) return

    navigator.clipboard.writeText(view.url)
    openSnackbar({ severity: 'success', title: 'Link to Certificate copied to clipboard' })
  }

  const allViewers = viewers ?? []
  const visibleViewers = showAllViewers ? allViewers : allViewers.slice(0, PREVIEW_COUNT)

  return (
    <DrawerPopup
      title={mode === 'create' ? 'Add custom view' : 'View settings'}
      handleOpen={open}
      handleClose={onClose}
      rightActionLabel='Cancel'
      rightActionOnClick={onClose}
      child={
        <Stack sx={{ pb: 2 }}>
          {/* There is no link to show or copy until the view is created. */}
          {mode !== 'create' && (
            <>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ mb: 2, minWidth: 0 }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    color: theme.palette.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {view?.url}
                </Typography>
              </Stack>

              <Button
                fullWidth
                variant='outlined'
                startIcon={<Link2 size={18} />}
                onClick={copyLink}
                sx={{
                  borderColor: theme.palette.grey[300],
                  color: theme.palette.text.primary,
                  borderRadius: '16px',
                  textTransform: 'none',
                  py: 1.25,
                  mb: 3,
                  '&:hover': { backgroundColor: theme.palette.grey[50] },
                }}
              >
                Copy link
              </Button>
            </>
          )}

          {/* VERA-298: tabs removed. A view has ~6 settings total, so tabs over
              1-4 items each is over-structure — worse, they HID half the decision:
              you couldn't see who has access while choosing what they see, and the
              two are coupled ("show sale prices" means something different on an
              invited link than on a public one). One scrolling form instead, in
              this order: Name -> Access -> Privacy. */}

          {/* Public view's name isn't user-set, so it has nothing to rename. */}
          {!(mode === 'edit' && view?.kind === 'public') && (
            <Box sx={{ pb: 3, mb: 1, borderBottom: `1px solid ${theme.palette.grey[200]}` }}>
              <Input
                label='Name'
                value={name}
                onChange={setName}
                placeholder='View label'
              />
            </Box>
          )}

          {/* Access shows in BOTH modes. In create there is no view id to attach
              viewers to yet, so the emails are collected here and sent by the caller
              immediately after the view is created: setting up a link for your insurer
              and inviting them is one intention, and splitting it across two visits to
              the same drawer served the data model rather than the user.

              The Invite BUTTON and the viewer list are edit-only. In create, "Create"
              is the single commit point for the whole form, so a second submit inside
              it would be ambiguous, and there is nobody to list yet. */}
          <Box sx={{ pb: 3, mb: 1, borderBottom: `1px solid ${theme.palette.grey[200]}` }}>
            {/*
              FieldLabel, not a bold `subtitle1`.

              Each of these names exactly ONE control — a two-card choice, an
              email input, a list — so they are FIELD labels, and this app's
              field labels come from one component (§31/§47). `subtitle1` resolves
              to full bold here (see the FONT_WEIGHTS note in createMuiTheme: a
              missing 600 snaps UP to 700), so this drawer was shouting three
              headings while EditEmailDrawer, PhoneVerificationDrawer and every
              Input in the app render theirs quietly through FieldLabel.
            */}
            <FieldLabel label='Who can open this link' />

            {/* VERA-300 — the way back to `anyone`.
                Inviting someone flips a view to `invited` implicitly, and until now
                nothing flipped it back: removing the last viewer left a link that was
                restricted to an empty allowlist, so it opened for NOBODY, with no
                control anywhere in the UI to undo that. Failing closed was right; being
                unable to reopen was not.

                Acts immediately rather than on Save, matching the rest of this section:
                an invite sends and a removal revokes on click, so a deferred access
                change would misrepresent when access actually changed. */}
            <Stack spacing={1} sx={{ mb: 3 }}>
              {[
                {
                  value: CertificateViewAccess.Anyone,
                  title: 'Anyone with the link',
                  description: 'No sign-in needed. Anyone you send it to can open it.',
                },
                {
                  value: CertificateViewAccess.Invited,
                  title: 'Only invited people',
                  description:
                    'They must be signed in with an invited email. Everyone else sees nothing.',
                },
              ].map((option) => {
                const selected = (view?.access ?? CertificateViewAccess.Anyone) === option.value
                /*
                 * `apps/app` has no separate `brand` palette — the tenant's
                 * `primary` IS the brand (settings/theme.ts), so this reads it
                 * directly instead of the `palette.brand?.[600] || primary.main`
                 * dance, which only ever took the fallback.
                 */
                const brand = theme.palette.primary.main

                return (
                  <Box
                    key={option.value}
                    component='button'
                    onClick={() => !selected && onAccessChange?.(option.value)}
                    /*
                     * borderColor is INLINE, and it has to be.
                     *
                     * Tailwind's preflight sets `*,::before,::after { border-color }`
                     * to zinc-200, and inside the shadow host that rule carries ID
                     * specificity — so it beat Emotion's single class and BOTH cards
                     * rendered rgb(228,228,231). The selected card's brand border had
                     * never once appeared: measured identical on both, which is why
                     * the group read as "one greyed-out card and one normal card"
                     * rather than as a choice. The tint was landing (backgrounds did
                     * differ), so nothing about the sx block looked wrong.
                     *
                     * An inline style outranks any selector, ID or not.
                     */
                    style={{ borderColor: selected ? brand : theme.palette.grey[300] }}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      font: 'inherit',
                      cursor: selected ? 'default' : 'pointer',
                      p: 2,
                      borderRadius: '16px',
                      // Selectable-card treatment (working-conventions §34): a brand
                      // BORDER means chosen, a tint alone means considering. Using a
                      // border for hover here would collide with the selected state.
                      borderStyle: 'solid',
                      borderWidth: '1px',
                      backgroundColor: selected ? alpha(brand, 0.1) : 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(brand, selected ? 0.1 : 0.06),
                      },
                      '&:focus-visible': {
                        outline: 'none',
                        boxShadow: `0 0 0 3px ${alpha(brand, 0.16)}`,
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: 16, mb: 0.5 }}>{option.title}</Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                )
              })}
            </Stack>

            <FieldLabel label='Invite viewers' />

            <Input
              value={emailsRaw}
              onChange={(value: string) => setEmailsRaw(value)}
              placeholder='Type or paste emails'
            />

            <Typography
              variant='body2'
              sx={{ color: theme.palette.text.secondary, lineHeight: 1.5, mt: 1 }}
            >
              To invite multiple people, separate the emails by comma, space, or new
              line.
            </Typography>

            {mode !== 'create' && (
              <Button
                fullWidth
                variant='contained'
                disabled={isInviting || parseInviteEmails(emailsRaw).length === 0}
                onClick={sendInvites}
                sx={{
                  mt: 2,
                  backgroundColor: theme.palette.common.black,
                  color: theme.palette.common.white,
                  borderRadius: '16px',
                  textTransform: 'none',
                  py: 1.25,
                  '&:hover': { backgroundColor: theme.palette.grey[800] },
                }}
              >
                Invite
              </Button>
            )}

            {mode !== 'create' && (
              <>
              <Box sx={{ mt: 3 }}>
                <FieldLabel label='Viewers' />
              </Box>

              {allViewers.length === 0 ? (
                <Typography
                  variant='body2'
                  sx={{ color: theme.palette.text.secondary, py: 1 }}
                >
                  No one has been invited yet. This link is open to anyone who has it.
                </Typography>
              ) : (
                <>
                  {visibleViewers.map((viewer) => (
                    <Stack
                      key={viewer.id}
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      spacing={2}
                      sx={{ py: 1.5, borderTop: `1px solid ${theme.palette.grey[200]}` }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 16,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {viewer.email}
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {viewer.last_viewed_at
                            ? `Last viewed ${timeAgo(viewer.last_viewed_at)}`
                            : `Invited ${timeAgo(viewer.invited_at)}`}
                        </Typography>
                      </Box>

                      <Button
                        size='small'
                        aria-label={`Remove ${viewer.email}`}
                        onClick={() => removeViewer(viewer.id)}
                        sx={{ minWidth: 0, color: theme.palette.text.secondary }}
                      >
                        <X size={18} />
                      </Button>
                    </Stack>
                  ))}

                  {/* Unbounded viewer lists would push the Privacy toggles off-screen,
                      so this caps the same way PublicCertificateSections does. */}
                  {allViewers.length > PREVIEW_COUNT && (
                    <Button
                      variant='text'
                      onClick={() => setShowAllViewers((v) => !v)}
                      sx={{ textTransform: 'none', px: 0, mt: 1 }}
                    >
                      {showAllViewers ? 'Show less' : `Show all ${allViewers.length}`}
                    </Button>
                  )}
                </>
              )}
              </>
            )}
          </Box>

          {MODULE_GROUPS.map((group) => (
            <Box key={group.section} sx={{ mb: 1 }}>
              <Typography
                variant='body2'
                sx={{
                  mt: 2,
                  mb: 1,
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 500,
                }}
              >
                {group.section}
              </Typography>

              {group.modules.map((module) => {
                // Prices render inside the history rows, so they cannot stand alone.
                // Scans are independent and stay enabled.
                const disabled = module.key === 'show_prices' && !draft.show_provenance

                return (
                  <Stack
                    key={module.key}
                    direction='row'
                    alignItems='flex-start'
                    justifyContent='space-between'
                    spacing={3}
                    sx={{ py: 3, borderTop: `1px solid ${theme.palette.grey[200]}` }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 16, lineHeight: '20px', mb: 1 }}>
                        {module.title}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}
                      >
                        {module.description}
                      </Typography>
                    </Box>

                    <Switch
                      sx={{ mt: '-2px' }}
                      checked={draft[module.key]}
                      disabled={disabled}
                      onChange={(_, checked) => setModule(module.key, checked)}
                    />
                  </Stack>
                )
              })}
            </Box>
          ))}

          {/* Save/Create applies the Privacy toggles + name. Access acts
              immediately — an invite is sent and a removal revokes on click,
              so batching them behind Save would misrepresent when access
              actually changes. A nameless view is exactly the "View 4"
              auto-name problem this form replaces, so the name is required
              wherever it's shown (hidden only for the public view). */}
          {/* Gap is 1.5, not 3. working-conventions §29 asks for a 12px button-group
              gap and gives `spacing={3}` for it, but that number assumes the @repo/ui
              theme's 4px base. apps/app never overrides MUI's default 8px, so `={3}`
              renders 24px here. 1.5 x 8 = the 12px the rule actually wants. */}
          {mode === 'create' ? (
            <Stack direction='row' spacing={1.5} sx={{ mt: 3 }}>
              <Button
                fullWidth
                variant='outlined'
                onClick={onClose}
                sx={{
                  borderColor: theme.palette.grey[300],
                  color: theme.palette.text.primary,
                  borderRadius: '16px',
                  textTransform: 'none',
                  py: 1.5,
                  '&:hover': { backgroundColor: theme.palette.grey[50] },
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant='contained'
                disabled={isSaving || name.trim().length === 0}
                onClick={() => onSave({
              name: name.trim(),
              visibility: draft,
              inviteEmails: mode === 'create' ? parseInviteEmails(emailsRaw) : [],
            })}
                sx={{
                  backgroundColor: theme.palette.common.black,
                  color: theme.palette.common.white,
                  borderRadius: '16px',
                  textTransform: 'none',
                  py: 1.5,
                  '&:hover': { backgroundColor: theme.palette.grey[800] },
                }}
              >
                Create
              </Button>
            </Stack>
          ) : (
            <Button
              fullWidth
              variant='contained'
              disabled={isSaving || (view?.kind !== 'public' && name.trim().length === 0)}
              onClick={() => onSave({
              name: name.trim(),
              visibility: draft,
              inviteEmails: mode === 'create' ? parseInviteEmails(emailsRaw) : [],
            })}
              sx={{
                mt: 3,
                backgroundColor: theme.palette.common.black,
                color: theme.palette.common.white,
                borderRadius: '16px',
                textTransform: 'none',
                py: 1.5,
                '&:hover': { backgroundColor: theme.palette.grey[800] },
              }}
            >
              Save
            </Button>
          )}
        </Stack>
      }
    />
  )
}
